// Custom hook for initializing staff/physician/anesthesiologist beacons

import { useState, useCallback } from 'react';
import { TEST_BEACONS } from '../constants';
import type { Physician, Anesthesiologist, Staff } from '../types';

export interface UseStaffBeaconInitReturn {
  initializingStaffBeacons: boolean;
  staffBeaconStatus: string | null;
  initializeStaffBeacons: () => Promise<void>;
}

interface UseStaffBeaconInitProps {
  staff: Staff[];
  physicians: Physician[];
  anesthesiologists: Anesthesiologist[];
  editStaff: (options: unknown) => Promise<unknown>;
  editPhysician: (options: unknown) => Promise<unknown>;
  editAnesthesiologist: (options: unknown) => Promise<unknown>;
  refetchStaff: () => void;
  refetchPhysicians: () => void;
  refetchAnesthesiologists: () => void;
}

export const useStaffBeaconInit = ({
  staff,
  physicians,
  anesthesiologists,
  editStaff,
  editPhysician,
  editAnesthesiologist,
  refetchStaff,
  refetchPhysicians,
  refetchAnesthesiologists,
}: UseStaffBeaconInitProps): UseStaffBeaconInitReturn => {
  const [initializingStaffBeacons, setInitializingStaffBeacons] = useState(false);
  const [staffBeaconStatus, setStaffBeaconStatus] = useState<string | null>(null);

  const initializeStaffBeacons = useCallback(async () => {
    setInitializingStaffBeacons(true);
    setStaffBeaconStatus('Initializing staff/physician beacons...');

    const results: { type: string; status: string; reason?: string }[] = [];

    // Get beacons by type
    const staffBeacons = TEST_BEACONS.filter(b => b.type === 'staff');
    const physicianBeacons = TEST_BEACONS.filter(b => b.type === 'physician');
    const anesthesiologistBeacons = TEST_BEACONS.filter(b => b.type === 'anesthesiologist');

    // Assign staff beacons using staffId (integer) instead of id (composite string like "21.1")
    for (let i = 0; i < staffBeacons.length; i++) {
      const beacon = staffBeacons[i];
      const staffMember = staff[i];

      if (!staffMember) {
        results.push({ type: 'staff', status: 'skipped', reason: 'No staff member available' });
        continue;
      }

      // Check if already assigned
      if (staffMember.bracelet?.id === beacon.id) {
        results.push({ type: 'staff', status: 'skipped', reason: 'Already assigned' });
        continue;
      }

      try {
        await editStaff({
          variables: {
            id: staffMember.staffId, // Use staffId (integer), not id (string like "21.1")
            name: staffMember.name,
            braceletId: beacon.id,
          },
        });
        results.push({ type: 'staff', status: 'assigned' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        results.push({ type: 'staff', status: 'error', reason: errorMessage });
      }
    }

    // Assign physician beacons
    for (let i = 0; i < physicianBeacons.length; i++) {
      const beacon = physicianBeacons[i];
      const physician = physicians[i];

      if (!physician) {
        results.push({ type: 'physician', status: 'skipped', reason: 'No physician available' });
        continue;
      }

      // Check if already assigned
      if (physician.bracelet?.id === beacon.id) {
        results.push({ type: 'physician', status: 'skipped', reason: 'Already assigned' });
        continue;
      }

      try {
        await editPhysician({
          variables: {
            id: physician.id,
            name: physician.name,
            braceletId: beacon.id,
          },
        });
        results.push({ type: 'physician', status: 'assigned' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        results.push({ type: 'physician', status: 'error', reason: errorMessage });
      }
    }

    // Assign anesthesiologist beacons
    for (let i = 0; i < anesthesiologistBeacons.length; i++) {
      const beacon = anesthesiologistBeacons[i];
      const anesthesiologist = anesthesiologists[i];

      if (!anesthesiologist) {
        results.push({ type: 'anesthesiologist', status: 'skipped', reason: 'No anesthesiologist available' });
        continue;
      }

      // Check if already assigned
      if (anesthesiologist.bracelet?.id === beacon.id) {
        results.push({ type: 'anesthesiologist', status: 'skipped', reason: 'Already assigned' });
        continue;
      }

      try {
        await editAnesthesiologist({
          variables: {
            id: anesthesiologist.id,
            name: anesthesiologist.name,
            braceletId: beacon.id,
          },
        });
        results.push({ type: 'anesthesiologist', status: 'assigned' });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        results.push({ type: 'anesthesiologist', status: 'error', reason: errorMessage });
      }
    }

    const assigned = results.filter(r => r.status === 'assigned').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    const errors = results.filter(r => r.status === 'error').length;

    setStaffBeaconStatus(`Done! Assigned: ${assigned}, Skipped: ${skipped}, Errors: ${errors}`);
    setInitializingStaffBeacons(false);

    // Refetch to see updated assignments
    setTimeout(() => {
      refetchStaff();
      refetchPhysicians();
      refetchAnesthesiologists();
    }, 1000);
  }, [
    staff,
    physicians,
    anesthesiologists,
    editStaff,
    editPhysician,
    editAnesthesiologist,
    refetchStaff,
    refetchPhysicians,
    refetchAnesthesiologists,
  ]);

  return {
    initializingStaffBeacons,
    staffBeaconStatus,
    initializeStaffBeacons,
  };
};
